/*
 * OemStatus.jsx
 * Copyright (c) Harman International Industries, Incorporated, 2024 - 2025
 * All rights reserved.
 */
import { Backdrop, CircularProgress, Container } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import Topheader from "../../../Layout/header/header";
import "./OemStatus.css";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import ApplicationDetailsSideMenu from "../../ApplicationDetailsSideMenu/ApplicationDetailsSideMenu";
import { fetchApplicationDetails } from "../../../Services/redux/appId-details/AppIdDeatilsActions";
import { fetchSubmissionDetails } from "../../../Services/redux/Application-details/ApplicationDetailsActions";
import { fetchAllSubmissions } from "../../../Services/redux/ApplicationDetailsSubmissions/ApplicationDetailsSubmissionsActions";
import APIManager from "../../../Services/ApiManager/ApiManager";
const STATUS_TABS = [
  { key: "APPROVED", label: "Approved" },
  { key: "IN_REVIEW", label: "In-Review" },
  { key: "REJECTED", label: "Rejected" },
  { key: "DELISTED", label: "Delisted" },
];

const APPROVED_STATUSES = [
  "APPROVED_FOR_BETA",
  "STAGING_APPROVAL_APPROVED",
  "PRODUCTION_APPROVAL_APPROVED",
];

const IN_REVIEW_STATUSES = [
  "STAGING_APPROVAL_IN_REVIEW",
  "PRODUCTION_APPROVAL_IN_REVIEW",
];

const REJECTED_STATUSES = [
  "REJECTED_AT_STAGING",
  "REJECTED_AT_PRODUCTION",
];

const DELISTED_STATUSES = [
  "FORCE_UNINSTALL_AT_STAGING",
  "DELIST_AT_STAGING",
  "FORCE_UNINSTALL_AT_PRODUCTION",
  "DELIST_AT_PRODUCTION",
  "GLOBAL_FORCE_UNINSTALL",
];

const TAB_STATUS_MAP = {
  APPROVED: APPROVED_STATUSES,
  IN_REVIEW: IN_REVIEW_STATUSES,
  REJECTED: REJECTED_STATUSES,
  DELISTED: DELISTED_STATUSES,
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const day = String(date.getDate()).padStart(2, "0");
  const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const month = months[date.getMonth()];
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
};

export default function OemStatus() {
  const { appId, submissionId } = useParams();
  const { state: locationState } = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [oemStatusData, setOemStatusData] = useState([]);
  const [loader, setLoader] = useState(true);
  const [activeTab, setActiveTab] = useState("APPROVED");

  const appData = useSelector(
    (state) => state.applicationDetails.applicationDetails,
  );

  const submissionData = useSelector(
    (state) => state.submissionDetails.submissionDetails,
  );

  const callNewSubmissionId = useCallback(
    (newSubmissionId) => {
      dispatch(fetchSubmissionDetails(appId, newSubmissionId));
    },
    [dispatch, appId],
  );

  const teamId = useMemo(
    () =>
      locationState?.teamId ||
      sessionStorage.getItem("teamId") ||
      appData?.teamId,
    [locationState?.teamId, appData?.teamId],
  );

  useEffect(() => {
    if (teamId) {
      sessionStorage.setItem("teamId", teamId);
    }
  }, [teamId]);

  const backToApplications = useCallback(() => {
    navigate(`/ui/developer/applications/${appId}`, {
      state: {
        teamId,
        submissionId,
      },
    });
  }, [navigate, appId, teamId, submissionId]);

  useEffect(() => {
    sessionStorage.setItem("oemStatusSubmissionId", submissionId);
    dispatch(fetchApplicationDetails(appId));
    dispatch(fetchSubmissionDetails(appId, submissionId));
    dispatch(fetchAllSubmissions(appId));
  }, [dispatch, appId, submissionId]);

  useEffect(() => {
    if (
      submissionData?.id !== submissionId &&
      submissionData?._id !== submissionId
    ) {
      return;
    }
    const packageName = submissionData?.binary?.packageName || submissionData?.bundle?.packageName;
    const appVersion =
      submissionData?.binary?.versionName ||
      submissionData?.versionName ||
      submissionData?.contentVersion || 
      submissionData.name;
    if (!packageName || !appVersion) {
      setLoader(false)
      return;
    }
    setLoader(true);
    APIManager.getAppOemStatus(packageName, appVersion)
      .then((data) => {
        setOemStatusData(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        setOemStatusData([]);
      })
      .finally(() => {
        setLoader(false);
      });
  }, [submissionData, submissionId]);

  const filteredStatusData = useMemo(() => {
    const rows = [];
    const allowedStatuses = TAB_STATUS_MAP[activeTab] || [];
    oemStatusData.forEach((oem) => {
      const statuses = oem.portfolioStatus || [];
      statuses.forEach((item) => {
        if (allowedStatuses.includes(item.status)) {
          rows.push({
            oem: oem.portfolioId,
            model: item.model,
            date: item.date,
          });
        }
      });
    });
    return rows;
  }, [oemStatusData, activeTab]);

  const renderOemContent = () => {
    if (loader) {
      return null;
    }
    if (oemStatusData.length === 0) {
      return <div className="oem-status-no-data">No OEMs available</div>;
    }
    return (
      <>
        <h3 className="oem-details-title">{"OEM's Activity Status"}</h3>

        <div className="oem-status-tabs">
          <span className="oem-status-tabs-label">Status:</span>
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              className={`oem-status-tab ${activeTab === tab.key ? "oem-status-tab-active" : ""}`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="oem-status-table-container">
          <table className="oem-status-table">
            <thead>
              <tr>
                <th>OEM</th>
                <th>Model</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {filteredStatusData.length > 0 ? (
                filteredStatusData.map((row, index) => (
                  <tr key={`${row.oem}-${row.model}-${row.date}-${index}`}>
                    <td>{row.oem}</td>
                    <td>{row.model}</td>
                    <td>{formatDate(row.date)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="3" className="oem-status-no-data">
                    No data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </>
    );
  };

  return (
    <>
     <Topheader />
      {loader && (
        <Backdrop
          sx={(theme) => ({ color: "#fff", zIndex: theme.zIndex.drawer + 1 })}
          open={loader}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
     
      {!loader && appData && Object.keys(appData).length > 0 ? (
        <div className="container-my-release">
          <div className="side-menu-my-release oem-status-side-menu">
            <button
              type="button"
              className="arrow-back oem-status-back-btn"
              onClick={backToApplications}
            >
              <div className="oem-status-back-icon">
                <ArrowBackIcon className="oem-status-arrow-icon" />
              </div>
              <div className="my-apps">Back to App Details</div>
            </button>
            <ApplicationDetailsSideMenu
              appData={appData}
              callNewSubmissionId={callNewSubmissionId}
              appMultiSubmissionId={submissionId}
              submissionDetails={submissionData}
            />
          </div>
          <Container maxWidth="lg">
            <div className="oem-status-content">
              {submissionData && Object.keys(submissionData).length > 0 && submissionData.icon && appData && Object.keys(appData).length > 0 && (
                <div className="oem-status-app-header">
                  <div className="oem-status-app-icon-name">
                    {submissionData.icon?.url && (
                      <img
                        style={{ borderRadius: "4px" }}
                        src={submissionData.icon.url}
                        alt={submissionData.fileName}
                        width={60}
                        height={60}
                      />
                    )}
                    <div style={{ marginLeft: submissionData.icon?.url ? "5px" : "0" }}>
                      <p className="application-name">{appData.name}</p>
                    </div>
                  </div>
                </div>
              )}
              {renderOemContent()}
            </div>
          </Container>
        </div>
      ) : null}
    </>
  );
}
